import GoogleTagManager from 'react-gtm-module';

type DataLayerPayload = Record<string, string | number>;

export enum Provider {
  TAG_MANAGER = 'tag_manager',
  BREAK = 'break',
}

interface Options {
  callback?: () => void;
  once?: boolean;
  provider?: Provider;
}

const ONCE_CACHE: { [key: string]: true } = {};

/**
 * @internal: Do not use this function directly, but create an own function with explicit types
 *
 * @param event: string (event name)
 * @param payload
 * @param callback
 * @param once: Track event only once per pathname, id and action
 */
export function track(
  event: string,
  payload?: DataLayerPayload,
  { callback, once, provider = Provider.TAG_MANAGER }: Options = {}
): void {
  if (typeof window !== 'undefined') {
    if (window['debugTracking']) {
      // eslint-disable-next-line no-console
      console.log('Track: ', event, payload);
    }

    // Once
    if (once) {
      const onceCacheKeyName = `${location.pathname}!${event}!${payload?.id}!${payload?.action}`;

      if (ONCE_CACHE[onceCacheKeyName]) {
        return;
      }

      ONCE_CACHE[onceCacheKeyName] = true;
    }

    const providers = {
      [Provider.TAG_MANAGER]: fireDataLayer,
      [Provider.BREAK]: fireBreakRequest,
    };

    if (providers[provider]) {
      providers[provider](event, payload, { callback });
    }
  }
}

const fireDataLayer = (event: string, payload?: DataLayerPayload, { callback }: Options = {}) => {
  // If a callback is provided and no dataLayer is available and we are in dev, resolve callback immediately
  if (callback && !window['dataLayer'] && process.env.NODE_ENV === 'development') {
    callback.bind(GoogleTagManager, [event, payload])();

    GoogleTagManager.dataLayer({
      dataLayer: { event, ...payload },
    });

    return;
  }

  GoogleTagManager.dataLayer({
    dataLayer: {
      event,
      ...(callback ? { eventTimeout: 120, eventCallback: callback.bind(GoogleTagManager, [event, payload]) } : {}),
      ...payload,
    },
  });
};

const fireBreakRequest = (_: string, payload?: DataLayerPayload) => {
  const breakUrl = new URL(`${location.origin}/de/home/misc/break.html`);
  const breakParams = new URLSearchParams(payload as Record<string, string>).toString();
  breakUrl.search = breakParams;

  fetch(breakUrl.toString(), { method: 'GET' });
};
